import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import {
  User,
  Video,
  Song,
  Audio,
  Book,
  Meditation,
  Promocode,
  Language,
  Translation,
  Tale,
  Lullaby,
} from '../types';
import { api } from '../config/api';

interface FirebaseTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

interface ApiResponse<T> {
  success: boolean;
  status: string;
  code: number;
  message: string;
  meta: {
    timestamp: number;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
  data: T;
}

// Add BookDetailsResponse type
export interface BookPage {
  pageNumber: number;
  image: string;
  audio?: string;
}

export interface AuthUserDetails {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  creationTime: string;
  emailVerified: boolean;
  lastSignInTime: string;
  customClaims?: Record<string, any>;
  disabled: boolean;
}

export interface UserDetails {
  id: string;
  email: string;
  childName: string;
  childAge: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  subscriptionStatus: string;
  subscriptionType: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  subscriptionPeriod: string;
  country?: string;
  region?: string;
  platform?: string;
  appVersion?: string;
  deviceInfo?: string;
  deviceType?: string;
  verified: boolean;
  subscriptionPromoCode: string;
  timezone: string;
}

export interface PromocodeDetails {
  id: string;
  code: string;
  type: string;
  value: number;
  maxUses: number;
  maxUsesPerUser: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  externalPayment: boolean;
  countEntered: number;
  countPurchased: number;
  isUsed: boolean;
  status: string;
  productPackage: string;
  period: string;
  usableAmount: number;
  isReusable: boolean;
  expire: string;
  sentBySms: boolean;
  extras?: Record<string, unknown>;
}

/**
 * Server-side query for the promocodes list. Filters and pagination are both
 * applied by the API, so anything omitted here falls back to the last query.
 */
export interface PromocodeQuery {
  /** Refetch even when the list has already been loaded once. */
  force?: boolean;
  /** Case-sensitive prefix match on the promocode itself. */
  search?: string;
  status?: string;
  type?: string;
  productPackage?: string;
  period?: string;
  /** Zero-based, matching the DataGrid's pagination model. */
  page?: number;
  pageSize?: number;
}

export interface BookDetailsResponse {
  title: string;
  coverImage?: string;
  coverAudio?: string;
  author?: string;
  description?: string;
  genre?: string;
  pageCount?: number;
  language?: string;
  pages: BookPage[];
  tags?: string[];
  status?: 'active' | 'inactive' | 'archived' | 'draft';
}

// Add MeditationDetailsResponse type
export interface MeditationDetailsResponse {
  title: string;
  coverImage?: string;
  coverAudio?: string;
  image?: string;
  audio?: string;
  instructor?: string;
  description?: string;
  type?: string;
  difficultyLevel?: string;
  language?: string;
  benefits?: string[];
  tags?: string[];
  status?: 'active' | 'inactive' | 'archived' | 'draft';
}

// Add AudioDetailsResponse type
export interface AudioDetailsResponse {
  title: string;
  coverImage?: string;
  coverAudio?: string;
  image?: string;
  audio?: string;
  artist?: string;
  album?: string;
  description?: string;
  genre?: string;
  language?: string;
  tags?: string[];
  status?: 'active' | 'inactive' | 'archived' | 'draft';
}

export interface SongDetailsResponse {
  title: string;
  coverImage?: string;
  coverAudio?: string;
  song?: string;
  artist?: string;
  album?: string;
  description?: string;
  category?: string;
  genre?: string;
  language?: string;
  tags?: string[];
  status?: 'active' | 'inactive' | 'archived' | 'draft';
}

export interface VideoDetailsResponse {
  title: string;
  coverImage?: string;
  coverAudio?: string;
  video?: string;
  description?: string;
  category?: string;
  language?: string;
  tags?: string[];
  status?: 'active' | 'inactive' | 'archived' | 'draft';
}

export interface LullabyDetailsResponse {
  title: string;
  coverImage?: string;
  coverAudio?: string;
  image?: string;
  lullaby?: string;
  description?: string;
  category?: string;
  artist?: string;
  composer?: string;
  genre?: string;
  language?: string;
  tags?: string[];
  status?: 'active' | 'inactive' | 'archived' | 'draft';
  ageGroup?: string;
}

export interface TaleDetailsResponse {
  title: string;
  coverImage?: string;
  coverAudio?: string;
  tale?: string;
  taleImage?: string;
  description?: string;
  category?: string;
  narrator?: string;
  series?: string;
  isVideoTale?: boolean;
  genre?: string;
  language?: string;
  tags?: string[];
  status?: 'active' | 'inactive' | 'archived' | 'draft';
  ageGroup?: string;
}

interface CollectionsContextType {
  // Users
  users: User[];
  usersLoading: boolean;
  usersError: string | null;
  usersPagination: PaginationState;
  fetchUsers: (force?: boolean, search?: string, page?: number, pageSize?: number) => Promise<void>;
  updateUser: (id: string, userData: Partial<User>) => Promise<void>;

  // Videos
  videos: Video[];
  videosLoading: boolean;
  videosError: string | null;
  videosPagination: PaginationState;
  fetchVideos: (
    force?: boolean,
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateVideo: (id: string, videoData: Partial<Video>) => Promise<void>;
  createVideo: (videoData: Omit<Video, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Songs
  songs: Song[];
  songsLoading: boolean;
  songsError: string | null;
  songsPagination: PaginationState;
  fetchSongs: (
    force?: boolean,
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateSong: (id: string, songData: Partial<Song>) => Promise<void>;
  createSong: (songData: Omit<Song, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Tales
  tales: Tale[];
  talesLoading: boolean;
  talesError: string | null;
  talesPagination: PaginationState;
  fetchTales: (
    force?: boolean,
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateTale: (id: string, taleData: Partial<Tale>) => Promise<void>;
  createTale: (taleData: Omit<Tale, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Lullabies
  lullabies: Lullaby[];
  lullabiesLoading: boolean;
  lullabiesError: string | null;
  lullabiesPagination: PaginationState;
  fetchLullabies: (
    force?: boolean,
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateLullaby: (id: string, lullabyData: Partial<Lullaby>) => Promise<void>;
  createLullaby: (lullabyData: Omit<Lullaby, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Audios
  audios: Audio[];
  audiosLoading: boolean;
  audiosError: string | null;
  audiosPagination: PaginationState;
  fetchAudios: (
    force?: boolean,
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateAudio: (id: string, audioData: Partial<Audio>) => Promise<void>;
  createAudio: (audioData: Omit<Audio, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Books
  books: Book[];
  booksLoading: boolean;
  booksError: string | null;
  booksPagination: PaginationState;
  fetchBooks: (
    force?: boolean,
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateBook: (id: string, bookData: Partial<Book>) => Promise<void>;
  createBook: (bookData: Omit<Book, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Meditations
  meditations: Meditation[];
  meditationsLoading: boolean;
  meditationsError: string | null;
  meditationsPagination: PaginationState;
  fetchMeditations: (
    force?: boolean,
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateMeditation: (id: string, meditationData: Partial<Meditation>) => Promise<void>;
  createMeditation: (
    meditationData: Omit<Meditation, 'id' | 'createdAt' | 'updatedAt'>,
  ) => Promise<void>;

  // Promocodes
  promocodes: Promocode[];
  promocodesLoading: boolean;
  promocodesError: string | null;
  promocodesPagination: PaginationState;
  fetchPromocodes: (options?: PromocodeQuery) => Promise<void>;
  updatePromocode: (id: string, promocodeData: Partial<Promocode>) => Promise<void>;
  createPromocode: (
    promocodeData: Omit<
      Promocode,
      'id' | 'createdAt' | 'updatedAt' | 'countEntered' | 'countPurchased' | 'isUsed' | 'createdBy'
    >,
  ) => Promise<void>;

  // Languages
  languages: Language[];
  languagesLoading: boolean;
  languagesError: string | null;
  languagesPagination: PaginationState;
  fetchLanguages: (
    force?: boolean,
    search?: string,
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
  updateLanguage: (key: string, languageData: Partial<Language>) => Promise<void>;
  createLanguage: (languageData: Language) => Promise<void>;
  deleteLanguage: (key: string) => Promise<void>;
  deleteLanguages: (keys: string[]) => Promise<void>;

  // Translations
  translations: Translation[];
  translationsLoading: boolean;
  translationsError: string | null;
  translationsPagination: PaginationState;
  fetchTranslations: (
    force?: boolean,
    search?: string,
    page?: number,
    pageSize?: number,
    language?: string,
    category?: string,
    searchType?: 'prefix' | 'exact',
    description?: string,
  ) => Promise<void>;
  updateTranslation: (id: string, translationData: Partial<Translation>) => Promise<void>;
  createTranslation: (
    translationData: Omit<Translation, 'id' | 'createdAt' | 'updatedAt'>,
  ) => Promise<void>;
  deleteTranslation: (id: string) => Promise<void>;
  deleteTranslations: (ids: string[]) => Promise<void>;
  exportTranslations: () => Promise<any>;
  exportTranslationsByLanguage: (language: string) => Promise<any>;
  importTranslations: (data: any[]) => Promise<void>;

  // User details fetcher
  userDetails: UserDetails | null;
  userDetailsLoading: boolean;
  userDetailsError: string | null;
  fetchUserDetails: (id: string) => Promise<UserDetails>;

  // Promocode details fetcher
  promocodeDetails: PromocodeDetails | null;
  promocodeDetailsLoading: boolean;
  promocodeDetailsError: string | null;
  fetchPromocodeDetails: (id: string) => Promise<PromocodeDetails>;

  // Book details fetcher
  bookDetails: BookDetailsResponse | null;
  bookDetailsLoading: boolean;
  bookDetailsError: string | null;
  fetchBookDetails: (id: string) => Promise<BookDetailsResponse>;
  clearBookDetails: () => void;
  // Meditation details fetcher
  meditationDetails: MeditationDetailsResponse | null;
  meditationDetailsLoading: boolean;
  meditationDetailsError: string | null;
  fetchMeditationDetails: (id: string) => Promise<MeditationDetailsResponse>;
  clearMeditationDetails: () => void;
  // Audio details fetcher
  audioDetails: AudioDetailsResponse | null;
  audioDetailsLoading: boolean;
  audioDetailsError: string | null;
  fetchAudioDetails: (id: string) => Promise<AudioDetailsResponse>;
  clearAudioDetails: () => void;

  // Song details fetcher
  songDetails: SongDetailsResponse | null;
  songDetailsLoading: boolean;
  songDetailsError: string | null;
  fetchSongDetails: (id: string) => Promise<SongDetailsResponse>;
  clearSongDetails: () => void;
  // Video details fetcher
  videoDetails: VideoDetailsResponse | null;
  videoDetailsLoading: boolean;
  videoDetailsError: string | null;
  fetchVideoDetails: (id: string) => Promise<VideoDetailsResponse>;
  clearVideoDetails: () => void;
  // Tale details fetcher
  taleDetails: TaleDetailsResponse | null;
  taleDetailsLoading: boolean;
  taleDetailsError: string | null;
  fetchTaleDetails: (id: string) => Promise<TaleDetailsResponse>;
  clearTaleDetails: () => void;
  // Lullaby details fetcher
  lullabyDetails: LullabyDetailsResponse | null;
  lullabyDetailsLoading: boolean;
  lullabyDetailsError: string | null;
  fetchLullabyDetails: (id: string) => Promise<LullabyDetailsResponse>;
  clearLullabyDetails: () => void;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (context === undefined) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return context;
};

interface CollectionsProviderProps {
  children: ReactNode;
}

export const CollectionsProvider: React.FC<CollectionsProviderProps> = ({ children }) => {
  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersPagination, setUsersPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Videos state
  const [videos, setVideos] = useState<Video[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState<string | null>(null);
  const [videosPagination, setVideosPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Songs state
  const [songs, setSongs] = useState<Song[]>([]);
  const [songsLoading, setSongsLoading] = useState(false);
  const [songsError, setSongsError] = useState<string | null>(null);
  const [songsPagination, setSongsPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Tales state
  const [tales, setTales] = useState<Tale[]>([]);
  const [talesLoading, setTalesLoading] = useState(false);
  const [talesError, setTalesError] = useState<string | null>(null);
  const [talesPagination, setTalesPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Lullabies state
  const [lullabies, setLullabies] = useState<Lullaby[]>([]);
  const [lullabiesLoading, setLullabiesLoading] = useState(false);
  const [lullabiesError, setLullabiesError] = useState<string | null>(null);
  const [lullabiesPagination, setLullabiesPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Audios state
  const [audios, setAudios] = useState<Audio[]>([]);
  const [audiosLoading, setAudiosLoading] = useState(false);
  const [audiosError, setAudiosError] = useState<string | null>(null);
  const [audiosPagination, setAudiosPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Promocodes state
  const [promocodes, setPromocodes] = useState<Promocode[]>([]);
  const [promocodesLoading, setPromocodesLoading] = useState(false);
  const [promocodesError, setPromocodesError] = useState<string | null>(null);
  const [promocodesPagination, setPromocodesPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Books state
  const [bookDetails, setBookDetails] = useState<BookDetailsResponse | null>(null);
  const [bookDetailsLoading, setBookDetailsLoading] = useState(false);
  const [bookDetailsError, setBookDetailsError] = useState<string | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksError, setBooksError] = useState<string | null>(null);
  const [booksPagination, setBooksPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Meditation details state
  const [meditationDetails, setMeditationDetails] = useState<MeditationDetailsResponse | null>(
    null,
  );
  const [meditationDetailsLoading, setMeditationDetailsLoading] = useState(false);
  const [meditationDetailsError, setMeditationDetailsError] = useState<string | null>(null);

  // Meditations state
  const [meditations, setMeditations] = useState<Meditation[]>([]);
  const [meditationsLoading, setMeditationsLoading] = useState(false);
  const [meditationsError, setMeditationsError] = useState<string | null>(null);
  const [meditationsPagination, setMeditationsPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Languages state
  const [languages, setLanguages] = useState<Language[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesError, setLanguagesError] = useState<string | null>(null);
  const [languagesPagination, setLanguagesPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Translations state
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [translationsLoading, setTranslationsLoading] = useState(false);
  const [translationsError, setTranslationsError] = useState<string | null>(null);
  const [translationsPagination, setTranslationsPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Audio details state
  const [audioDetails, setAudioDetails] = useState<AudioDetailsResponse | null>(null);
  const [audioDetailsLoading, setAudioDetailsLoading] = useState(false);
  const [audioDetailsError, setAudioDetailsError] = useState<string | null>(null);

  // Song details state
  const [songDetails, setSongDetails] = useState<SongDetailsResponse | null>(null);
  const [songDetailsLoading, setSongDetailsLoading] = useState(false);
  const [songDetailsError, setSongDetailsError] = useState<string | null>(null);

  // Video details state
  const [videoDetails, setVideoDetails] = useState<VideoDetailsResponse | null>(null);
  const [videoDetailsLoading, setVideoDetailsLoading] = useState(false);
  const [videoDetailsError, setVideoDetailsError] = useState<string | null>(null);

  // Tale details state
  const [taleDetails, setTaleDetails] = useState<TaleDetailsResponse | null>(null);
  const [taleDetailsLoading, setTaleDetailsLoading] = useState(false);
  const [taleDetailsError, setTaleDetailsError] = useState<string | null>(null);

  // Lullaby details state
  const [lullabyDetails, setLullabyDetails] = useState<LullabyDetailsResponse | null>(null);
  const [lullabyDetailsLoading, setLullabyDetailsLoading] = useState(false);
  const [lullabyDetailsError, setLullabyDetailsError] = useState<string | null>(null);

  // User details state
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState<string | null>(null);

  // Promocode details state
  const [promocodeDetails, setPromocodeDetails] = useState<PromocodeDetails | null>(null);
  const [promocodeDetailsLoading, setPromocodeDetailsLoading] = useState(false);
  const [promocodeDetailsError, setPromocodeDetailsError] = useState<string | null>(null);

  // Refs to prevent duplicate calls during StrictMode
  const usersInitialized = useRef(false);
  const videosInitialized = useRef(false);
  const songsInitialized = useRef(false);
  const talesInitialized = useRef(false);
  const lullabiesInitialized = useRef(false);
  const audiosInitialized = useRef(false);
  const promocodesInitialized = useRef(false);
  // Last promocodes query, so pagination and filter changes compose instead of
  // resetting each other. Held in a ref to keep `fetchPromocodes` stable.
  const promocodesQuery = useRef<PromocodeQuery>({ page: 0, pageSize: 10 });
  const booksInitialized = useRef(false);
  const meditationsInitialized = useRef(false);
  const languagesInitialized = useRef(false);
  const translationsInitialized = useRef(false);

  // Users methods
  const fetchUsers = useCallback(
    async (force: boolean = false, search?: string, page?: number, pageSize?: number) => {
      if (!force && usersInitialized.current && !search && !page && !pageSize) return;

      try {
        setUsersLoading(true);
        setUsersError(null);

        // Build query parameters
        const params = new URLSearchParams();
        if (search && search.trim()) {
          params.append('search', search.trim());
        }

        // Use provided pagination or current state
        const currentPage = page !== undefined ? page + 1 : usersPagination.currentPage; // DataGrid uses 0-based, API uses 1-based
        const currentPageSize = pageSize !== undefined ? pageSize : usersPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/users?${queryString}`;

        const response = await api.get<
          ApiResponse<{
            users: Array<User & { createdAt: FirebaseTimestamp; updatedAt: FirebaseTimestamp }>;
          }>
        >(url);

        if (response.data.success) {
          setUsers(response.data.data.users);
          setUsersPagination(response.data.meta.pagination);
          if (!search && !page && !pageSize) {
            usersInitialized.current = true;
          }
        } else {
          setUsersError(response.data.message || 'Failed to fetch users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setUsersError('Failed to fetch users. Please try again later.');
      } finally {
        setUsersLoading(false);
      }
    },
    [usersPagination.currentPage, usersPagination.itemsPerPage],
  );

  const updateUser = useCallback(
    async (id: string, userData: Partial<User>) => {
      try {
        await api.put(`/api/users/${id}`, userData);
        await fetchUsers(true); // Force refresh
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    },
    [fetchUsers],
  );

  // Videos methods
  const fetchVideos = useCallback(
    async (
      force: boolean = false,
      filters?: Record<string, any>,
      page?: number,
      pageSize?: number,
    ) => {
      if (!force && videosInitialized.current && !filters && !page && !pageSize) return;

      try {
        setVideosLoading(true);
        setVideosError(null);

        // Build query parameters
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              params.append(key, value);
            }
          });
        }

        // Use provided pagination or current state
        const currentPage = page !== undefined ? page + 1 : videosPagination.currentPage;
        const currentPageSize = pageSize !== undefined ? pageSize : videosPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/videos?${queryString}`;

        const response = await api.get<
          ApiResponse<{
            videos: Array<Video & { createdAt: FirebaseTimestamp; updatedAt: FirebaseTimestamp }>;
          }>
        >(url);

        if (response.data.success) {
          setVideos(response.data.data.videos);
          setVideosPagination(response.data.meta.pagination);
          if (!filters && !page && !pageSize) {
            videosInitialized.current = true;
          }
        } else {
          setVideosError(response.data.message || 'Failed to fetch videos');
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        setVideosError('Failed to fetch videos. Please try again later.');
      } finally {
        setVideosLoading(false);
      }
    },
    [videosPagination.currentPage, videosPagination.itemsPerPage],
  );

  const updateVideo = useCallback(
    async (id: string, videoData: Partial<Video>) => {
      try {
        await api.put(`/api/videos/${id}`, videoData);
        await fetchVideos(true); // Force refresh
      } catch (error) {
        console.error('Error updating video:', error);
        throw error;
      }
    },
    [fetchVideos],
  );

  const createVideo = useCallback(
    async (videoData: Omit<Video, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/videos', videoData);
        await fetchVideos(true); // Force refresh
      } catch (error) {
        console.error('Error creating video:', error);
        throw error;
      }
    },
    [fetchVideos],
  );

  // Songs methods
  const fetchSongs = useCallback(
    async (
      force: boolean = false,
      filters?: Record<string, any>,
      page?: number,
      pageSize?: number,
    ) => {
      if (!force && songsInitialized.current && !filters && !page && !pageSize) return;

      try {
        setSongsLoading(true);
        setSongsError(null);

        // Build query parameters
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              params.append(key, value);
            }
          });
        }

        // Use provided pagination or current state
        const currentPage = page !== undefined ? page + 1 : songsPagination.currentPage;
        const currentPageSize = pageSize !== undefined ? pageSize : songsPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/songs?${queryString}`;

        const response = await api.get<ApiResponse<{ songs: Song[] }>>(url);

        if (response.data.success) {
          setSongs(response.data.data.songs);
          setSongsPagination(response.data.meta.pagination);
          if (!filters && !page && !pageSize) {
            songsInitialized.current = true;
          }
        } else {
          setSongsError(response.data.message || 'Failed to fetch songs');
        }
      } catch (err) {
        console.error('Error fetching songs:', err);
        setSongsError('Failed to fetch songs. Please try again later.');
      } finally {
        setSongsLoading(false);
      }
    },
    [songsPagination.currentPage, songsPagination.itemsPerPage],
  );

  const updateSong = useCallback(
    async (id: string, songData: Partial<Song>) => {
      try {
        await api.put(`/api/songs/${id}`, songData);
        await fetchSongs(true); // Force refresh
      } catch (error) {
        console.error('Error updating song:', error);
        throw error;
      }
    },
    [fetchSongs],
  );

  const createSong = useCallback(
    async (songData: Omit<Song, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/songs', songData);
        await fetchSongs(true); // Force refresh
      } catch (error) {
        console.error('Error creating song:', error);
        throw error;
      }
    },
    [fetchSongs],
  );

  // Tales methods
  const fetchTales = useCallback(
    async (
      force: boolean = false,
      filters?: Record<string, any>,
      page?: number,
      pageSize?: number,
    ) => {
      if (!force && talesInitialized.current && !filters && !page && !pageSize) return;

      try {
        setTalesLoading(true);
        setTalesError(null);

        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              params.append(key, value);
            }
          });
        }

        const currentPage = page !== undefined ? page + 1 : talesPagination.currentPage;
        const currentPageSize = pageSize !== undefined ? pageSize : talesPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/tales?${queryString}`;

        const response = await api.get<ApiResponse<{ tales: Tale[] }>>(url);

        if (response.data.success) {
          setTales(response.data.data.tales);
          setTalesPagination(response.data.meta.pagination);
          if (!filters && !page && !pageSize) {
            talesInitialized.current = true;
          }
        } else {
          setTalesError(response.data.message || 'Failed to fetch tales');
        }
      } catch (err) {
        console.error('Error fetching tales:', err);
        setTalesError('Failed to fetch tales. Please try again later.');
      } finally {
        setTalesLoading(false);
      }
    },
    [talesPagination.currentPage, talesPagination.itemsPerPage],
  );

  const updateTale = useCallback(
    async (id: string, taleData: Partial<Tale>) => {
      try {
        await api.put(`/api/tales/${id}`, taleData);
        await fetchTales(true);
      } catch (error) {
        console.error('Error updating tale:', error);
        throw error;
      }
    },
    [fetchTales],
  );

  const createTale = useCallback(
    async (taleData: Omit<Tale, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/tales', taleData);
        await fetchTales(true);
      } catch (error) {
        console.error('Error creating tale:', error);
        throw error;
      }
    },
    [fetchTales],
  );

  const fetchLullabies = useCallback(
    async (
      force: boolean = false,
      filters?: Record<string, any>,
      page?: number,
      pageSize?: number,
    ) => {
      if (!force && lullabiesInitialized.current && !filters && !page && !pageSize) return;

      try {
        setLullabiesLoading(true);
        setLullabiesError(null);

        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              params.append(key, value);
            }
          });
        }

        const currentPage = page !== undefined ? page + 1 : lullabiesPagination.currentPage;
        const currentPageSize =
          pageSize !== undefined ? pageSize : lullabiesPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/lullabies?${queryString}`;

        const response = await api.get<ApiResponse<{ lullabies: Lullaby[] }>>(url);

        if (response.data.success) {
          setLullabies(response.data.data.lullabies);
          setLullabiesPagination(response.data.meta.pagination);
          if (!filters && !page && !pageSize) {
            lullabiesInitialized.current = true;
          }
        } else {
          setLullabiesError(response.data.message || 'Failed to fetch lullabies');
        }
      } catch (err) {
        console.error('Error fetching lullabies:', err);
        setLullabiesError('Failed to fetch lullabies. Please try again later.');
      } finally {
        setLullabiesLoading(false);
      }
    },
    [lullabiesPagination.currentPage, lullabiesPagination.itemsPerPage],
  );

  const updateLullaby = useCallback(
    async (id: string, lullabyData: Partial<Lullaby>) => {
      try {
        await api.put(`/api/lullabies/${id}`, lullabyData);
        await fetchLullabies(true);
      } catch (error) {
        console.error('Error updating lullaby:', error);
        throw error;
      }
    },
    [fetchLullabies],
  );

  const createLullaby = useCallback(
    async (lullabyData: Omit<Lullaby, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/lullabies', lullabyData);
        await fetchLullabies(true);
      } catch (error) {
        console.error('Error creating lullaby:', error);
        throw error;
      }
    },
    [fetchLullabies],
  );

  // Audios methods
  const fetchAudios = useCallback(
    async (
      force: boolean = false,
      filters?: Record<string, any>,
      page?: number,
      pageSize?: number,
    ) => {
      if (!force && audiosInitialized.current && !filters && !page && !pageSize) return;

      try {
        setAudiosLoading(true);
        setAudiosError(null);

        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              params.append(key, value);
            }
          });
        }

        const currentPage = page !== undefined ? page + 1 : audiosPagination.currentPage;
        const currentPageSize = pageSize !== undefined ? pageSize : audiosPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/audios?${queryString}`;

        const response = await api.get<ApiResponse<{ audios: Audio[] }>>(url);

        if (response.data.success) {
          setAudios(response.data.data.audios);
          setAudiosPagination(response.data.meta.pagination);
          if (!filters && !page && !pageSize) {
            audiosInitialized.current = true;
          }
        } else {
          setAudiosError(response.data.message || 'Failed to fetch audios');
        }
      } catch (err) {
        console.error('Error fetching audios:', err);
        setAudiosError('Failed to fetch audios. Please try again later.');
      } finally {
        setAudiosLoading(false);
      }
    },
    [audiosPagination.currentPage, audiosPagination.itemsPerPage],
  );

  const updateAudio = useCallback(
    async (id: string, audioData: Partial<Audio>) => {
      try {
        await api.put(`/api/audios/${id}`, audioData);
        await fetchAudios(true);
      } catch (error) {
        console.error('Error updating audio:', error);
        throw error;
      }
    },
    [fetchAudios],
  );

  const createAudio = useCallback(
    async (audioData: Omit<Audio, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/audios', audioData);
        await fetchAudios(true);
      } catch (error) {
        console.error('Error creating audio:', error);
        throw error;
      }
    },
    [fetchAudios],
  );

  // Promocodes methods
  const fetchPromocodes = useCallback(async (options: PromocodeQuery = {}) => {
    const { force = false, page, pageSize, ...filters } = options;
    const hasFilterChange = Object.keys(filters).length > 0;

    if (!force && promocodesInitialized.current && !hasFilterChange && page === undefined) return;

    // Merge into the last query so changing the page keeps active filters, and
    // changing a filter keeps the page size.
    const query: PromocodeQuery = {
      ...promocodesQuery.current,
      ...filters,
      // Any filter change invalidates the current page offset.
      page: hasFilterChange ? 0 : (page ?? promocodesQuery.current.page ?? 0),
      pageSize: pageSize ?? promocodesQuery.current.pageSize ?? 10,
    };
    promocodesQuery.current = query;

    try {
      setPromocodesLoading(true);
      setPromocodesError(null);

      const params = new URLSearchParams();
      for (const key of ['search', 'status', 'type', 'productPackage', 'period'] as const) {
        const value = query[key]?.trim();
        if (value) params.append(key, value);
      }
      // The API is 1-based; the DataGrid is 0-based.
      params.append('page', String((query.page ?? 0) + 1));
      params.append('limit', String(query.pageSize));

      const url = `/api/promocodes?${params.toString()}`;

      const response = await api.get<ApiResponse<{ promocodes: Promocode[] }>>(url);

      if (response.data.success) {
        setPromocodes(response.data.data.promocodes);
        setPromocodesPagination(response.data.meta.pagination);
        promocodesInitialized.current = true;
      } else {
        setPromocodesError(response.data.message || 'Failed to fetch promocodes');
      }
    } catch (err) {
      console.error('Error fetching promocodes:', err);
      setPromocodesError('Failed to fetch promocodes. Please try again later.');
    } finally {
      setPromocodesLoading(false);
    }
  }, []);

  const updatePromocode = useCallback(
    async (id: string, promocodeData: Partial<Promocode>) => {
      try {
        await api.put(`/api/promocodes/${id}`, promocodeData);
        await fetchPromocodes({ force: true }); // Force refresh
      } catch (error) {
        console.error('Error updating promocode:', error);
        throw error;
      }
    },
    [fetchPromocodes],
  );

  const createPromocode = useCallback(
    async (
      promocodeData: Omit<
        Promocode,
        | 'id'
        | 'createdAt'
        | 'updatedAt'
        | 'countEntered'
        | 'countPurchased'
        | 'isUsed'
        | 'createdBy'
      >,
    ) => {
      try {
        await api.post('/api/promocodes', promocodeData);
        await fetchPromocodes({ force: true }); // Force refresh
      } catch (error) {
        console.error('Error creating promocode:', error);
        throw error;
      }
    },
    [fetchPromocodes],
  );

  // Books methods
  const fetchBooks = useCallback(
    async (
      force: boolean = false,
      filters?: Record<string, any>,
      page?: number,
      pageSize?: number,
    ) => {
      if (!force && booksInitialized.current && !filters && !page && !pageSize) return;

      try {
        setBooksLoading(true);
        setBooksError(null);

        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              params.append(key, value);
            }
          });
        }

        const currentPage = page !== undefined ? page + 1 : booksPagination.currentPage;
        const currentPageSize = pageSize !== undefined ? pageSize : booksPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/books?${queryString}`;

        const response = await api.get<ApiResponse<{ books: Book[] }>>(url);

        if (response.data.success) {
          setBooks(response.data.data.books);
          setBooksPagination(response.data.meta.pagination);
          if (!filters && !page && !pageSize) {
            booksInitialized.current = true;
          }
        } else {
          setBooksError(response.data.message || 'Failed to fetch books');
        }
      } catch (err) {
        console.error('Error fetching books:', err);
        setBooksError('Failed to fetch books. Please try again later.');
      } finally {
        setBooksLoading(false);
      }
    },
    [booksPagination.currentPage, booksPagination.itemsPerPage],
  );

  const updateBook = useCallback(
    async (id: string, bookData: Partial<Book>) => {
      try {
        await api.put(`/api/books/${id}`, bookData);
        await fetchBooks(true);
      } catch (error) {
        console.error('Error updating book:', error);
        throw error;
      }
    },
    [fetchBooks],
  );

  const createBook = useCallback(
    async (bookData: Omit<Book, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/books', bookData);
        await fetchBooks(true);
      } catch (error) {
        console.error('Error creating book:', error);
        throw error;
      }
    },
    [fetchBooks],
  );

  // Meditations methods
  const fetchMeditations = useCallback(
    async (
      force: boolean = false,
      filters?: Record<string, any>,
      page?: number,
      pageSize?: number,
    ) => {
      if (!force && meditationsInitialized.current && !filters && !page && !pageSize) return;

      try {
        setMeditationsLoading(true);
        setMeditationsError(null);

        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              params.append(key, value);
            }
          });
        }

        const currentPage = page !== undefined ? page + 1 : meditationsPagination.currentPage;
        const currentPageSize =
          pageSize !== undefined ? pageSize : meditationsPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/meditations?${queryString}`;

        const response = await api.get<ApiResponse<{ meditations: Meditation[] }>>(url);

        if (response.data.success) {
          setMeditations(response.data.data.meditations);
          setMeditationsPagination(response.data.meta.pagination);
          if (!filters && !page && !pageSize) {
            meditationsInitialized.current = true;
          }
        } else {
          setMeditationsError(response.data.message || 'Failed to fetch meditations');
        }
      } catch (err) {
        console.error('Error fetching meditations:', err);
        setMeditationsError('Failed to fetch meditations. Please try again later.');
      } finally {
        setMeditationsLoading(false);
      }
    },
    [meditationsPagination.currentPage, meditationsPagination.itemsPerPage],
  );

  const updateMeditation = useCallback(
    async (id: string, meditationData: Partial<Meditation>) => {
      try {
        await api.put(`/api/meditations/${id}`, meditationData);
        await fetchMeditations(true);
      } catch (error) {
        console.error('Error updating meditation:', error);
        throw error;
      }
    },
    [fetchMeditations],
  );

  const createMeditation = useCallback(
    async (meditationData: Omit<Meditation, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/meditations', meditationData);
        await fetchMeditations(true);
      } catch (error) {
        console.error('Error creating meditation:', error);
        throw error;
      }
    },
    [fetchMeditations],
  );

  // Languages methods (placeholder implementations)
  const fetchLanguages = useCallback(
    async (force: boolean = false, search?: string, page?: number, pageSize?: number) => {
      if (!force && languagesInitialized.current && !search && !page && !pageSize) return;

      try {
        setLanguagesLoading(true);
        setLanguagesError(null);

        const params = new URLSearchParams();
        if (search && search.trim()) {
          params.append('search', search.trim());
        }

        const currentPage = page !== undefined ? page + 1 : languagesPagination.currentPage;
        const currentPageSize =
          pageSize !== undefined ? pageSize : languagesPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/languages?${queryString}`;

        const response = await api.get<ApiResponse<{ languages: Language[] }>>(url);

        if (response.data.success) {
          setLanguages(response.data.data.languages);
          setLanguagesPagination(response.data.meta.pagination);
          if (!search && !page && !pageSize) {
            languagesInitialized.current = true;
          }
        } else {
          setLanguagesError(response.data.message || 'Failed to fetch languages');
        }
      } catch (err) {
        console.error('Error fetching languages:', err);
        setLanguagesError('Failed to fetch languages. Please try again later.');
      } finally {
        setLanguagesLoading(false);
      }
    },
    [languagesPagination.currentPage, languagesPagination.itemsPerPage],
  );

  const updateLanguage = useCallback(
    async (key: string, languageData: Partial<Language>) => {
      try {
        await api.put(`/api/languages/${key}`, languageData);
        // Reset initialization flag to force fresh fetch
        languagesInitialized.current = false;
        await fetchLanguages(true);
      } catch (error) {
        console.error('Error updating language:', error);
        throw error;
      }
    },
    [fetchLanguages],
  );

  const createLanguage = useCallback(
    async (languageData: Language) => {
      try {
        await api.post('/api/languages', languageData);
        // Reset initialization flag to force fresh fetch
        languagesInitialized.current = false;
        await fetchLanguages(true);
      } catch (error) {
        console.error('Error creating language:', error);
        throw error;
      }
    },
    [fetchLanguages],
  );

  const deleteLanguage = useCallback(
    async (key: string) => {
      try {
        await api.delete(`/api/languages/${key}`);
        // Reset initialization flag to force fresh fetch
        languagesInitialized.current = false;
        await fetchLanguages(true);
      } catch (error) {
        console.error('Error deleting language:', error);
        throw error;
      }
    },
    [fetchLanguages],
  );

  const deleteLanguages = useCallback(
    async (keys: string[]) => {
      try {
        await api.delete('/api/languages', { data: { ids: keys } });
        // Reset initialization flag to force fresh fetch
        languagesInitialized.current = false;
        await fetchLanguages(true);
      } catch (error) {
        console.error('Error deleting languages:', error);
        throw error;
      }
    },
    [fetchLanguages],
  );

  // Translations methods (placeholder implementations)
  const fetchTranslations = useCallback(
    async (
      force: boolean = false,
      search?: string,
      page?: number,
      pageSize?: number,
      language?: string,
      category?: string,
      searchType?: 'prefix' | 'exact',
      description?: string,
    ) => {
      if (
        !force &&
        translationsInitialized.current &&
        !search &&
        !page &&
        !pageSize &&
        !language &&
        !category &&
        !searchType &&
        !description
      )
        return;

      try {
        setTranslationsLoading(true);
        setTranslationsError(null);

        const params = new URLSearchParams();
        if (search && search.trim()) {
          params.append('search', search.trim());
        }
        if (language) {
          params.append('language', language);
        }
        if (category) {
          params.append('category', category);
        }
        if (searchType) {
          params.append('searchType', searchType);
        }
        if (description) {
          params.append('description', description);
        }

        const currentPage = page !== undefined ? page + 1 : translationsPagination.currentPage;
        const currentPageSize =
          pageSize !== undefined ? pageSize : translationsPagination.itemsPerPage;

        params.append('page', currentPage.toString());
        params.append('limit', currentPageSize.toString());

        const queryString = params.toString();
        const url = `/api/translations?${queryString}`;

        const response = await api.get<ApiResponse<{ translations: Translation[] }>>(url);

        if (response.data.success) {
          setTranslations(response.data.data.translations);
          setTranslationsPagination(response.data.meta.pagination);
          if (
            !search &&
            !page &&
            !pageSize &&
            !language &&
            !category &&
            !searchType &&
            !description
          ) {
            translationsInitialized.current = true;
          }
        } else {
          setTranslationsError(response.data.message || 'Failed to fetch translations');
        }
      } catch (err) {
        console.error('Error fetching translations:', err);
        setTranslationsError('Failed to fetch translations. Please try again later.');
      } finally {
        setTranslationsLoading(false);
      }
    },
    [translationsPagination.currentPage, translationsPagination.itemsPerPage],
  );

  const updateTranslation = useCallback(
    async (id: string, translationData: Partial<Translation>) => {
      try {
        await api.put(`/api/translations/${id}`, translationData);
        // Reset initialization flag to force fresh fetch
        translationsInitialized.current = false;
        await fetchTranslations(true, undefined, undefined, undefined, translationData.language);
      } catch (error) {
        console.error('Error updating translation:', error);
        throw error;
      }
    },
    [fetchTranslations],
  );

  const createTranslation = useCallback(
    async (translationData: Omit<Translation, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await api.post('/api/translations', translationData);
        // Reset initialization flag to force fresh fetch
        translationsInitialized.current = false;
        await fetchTranslations(true, undefined, undefined, undefined, translationData.language);
      } catch (error) {
        console.error('Error creating translation:', error);
        throw error;
      }
    },
    [fetchTranslations],
  );

  const deleteTranslation = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/api/translations/${id}`);
        // Reset initialization flag to force fresh fetch
        translationsInitialized.current = false;
        await fetchTranslations(true, undefined, undefined, undefined);
      } catch (error) {
        console.error('Error deleting translation:', error);
        throw error;
      }
    },
    [fetchTranslations],
  );

  const deleteTranslations = useCallback(
    async (ids: string[]) => {
      try {
        await api.delete('/api/translations', { data: { ids } });
        // Reset initialization flag to force fresh fetch
        translationsInitialized.current = false;
        await fetchTranslations(true);
      } catch (error) {
        console.error('Error deleting translations:', error);
        throw error;
      }
    },
    [fetchTranslations],
  );

  const exportTranslations = useCallback(async () => {
    try {
      const response = await api.get('/api/translations/export');
      return response.data;
    } catch (error) {
      console.error('Error exporting translations:', error);
      throw error;
    }
  }, []);

  const exportTranslationsByLanguage = useCallback(async (language: string) => {
    try {
      const response = await api.get(
        `/api/translations/export-by-language?language=${encodeURIComponent(language)}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting translations by language:', error);
      throw error;
    }
  }, []);

  const importTranslations = useCallback(
    async (data: any[]) => {
      try {
        await api.post('/api/translations/import', { data });
        await fetchTranslations(true);
      } catch (error) {
        console.error('Error importing translations:', error);
        throw error;
      }
    },
    [fetchTranslations],
  );

  // User details methods
  const fetchUserDetails = useCallback(async (id: string): Promise<UserDetails> => {
    try {
      setUserDetailsLoading(true);
      setUserDetailsError(null);

      const response = await api.get(`/api/users/${id}`);

      if (response.data.success) {
        setUserDetails(response.data.data);
        return response.data.data;
      } else {
        setUserDetailsError(response.data.message || 'Failed to fetch user details');
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      setUserDetailsError('Failed to fetch user details. Please try again later.');
    } finally {
      setUserDetailsLoading(false);
    }
  }, []);

  // Promocode details methods
  const fetchPromocodeDetails = useCallback(async (id: string): Promise<PromocodeDetails> => {
    try {
      setPromocodeDetailsLoading(true);
      setPromocodeDetailsError(null);

      const response = await api.get(`/api/promocodes/${id}`);

      if (response.data.success) {
        setPromocodeDetails(response.data.data);
        return response.data.data;
      } else {
        setPromocodeDetailsError(response.data.message || 'Failed to fetch promocode details');
      }
    } catch (err) {
      console.error('Error fetching promocode details:', err);
      setPromocodeDetailsError('Failed to fetch promocode details. Please try again later.');
    } finally {
      setPromocodeDetailsLoading(false);
    }
  }, []);

  // Book details fetcher
  const fetchBookDetails = useCallback(async (id: string): Promise<BookDetailsResponse> => {
    setBookDetailsLoading(true);
    try {
      const res = await api.get(`/api/books/${id}`);
      setBookDetails(res.data.data);
      return res.data.data;
    } catch (error) {
      console.error('CollectionsContext: Error fetching book details:', error);
      console.error('CollectionsContext: Error response:', error?.response);
      setBookDetailsError(error?.response?.data?.message || 'Failed to fetch book details');
      throw error;
    } finally {
      setBookDetailsLoading(false);
    }
  }, []);

  const clearBookDetails = useCallback(() => {
    setBookDetails(null);
  }, []);

  // Meditation details fetcher
  const fetchMeditationDetails = useCallback(
    async (id: string): Promise<MeditationDetailsResponse> => {
      setMeditationDetailsLoading(true);
      try {
        const res = await api.get(`/api/meditations/${id}`);
        setMeditationDetails(res.data.data);
        return res.data.data;
      } catch (error) {
        console.error('CollectionsContext: Error fetching meditation details:', error);
        console.error('CollectionsContext: Error response:', error?.response);
        setMeditationDetailsError(
          error?.response?.data?.message || 'Failed to fetch meditation details',
        );
        throw error;
      } finally {
        setMeditationDetailsLoading(false);
      }
    },
    [],
  );

  const clearMeditationDetails = useCallback(() => {
    setMeditationDetails(null);
  }, []);

  const fetchAudioDetails = useCallback(async (id: string) => {
    setAudioDetailsLoading(true);
    setAudioDetailsError(null);
    try {
      // Replace with your actual API call
      const response = await api.get(`/api/audios/${id}`);
      if (!response.data.success) throw new Error('Failed to fetch audio details');
      const data = response.data.data;
      setAudioDetails(data);
      setAudioDetailsLoading(false);
      return data;
    } catch (err: any) {
      setAudioDetailsError(err.message || 'Failed to fetch audio details');
      setAudioDetailsLoading(false);
      throw err;
    }
  }, []);

  const clearAudioDetails = useCallback(() => {
    setAudioDetails(null);
  }, []);

  const fetchSongDetails = useCallback(async (id: string) => {
    setSongDetailsLoading(true);
    setSongDetailsError(null);
    try {
      // Replace with your actual API call
      const response = await api.get(`/api/songs/${id}`);
      if (!response.data.success) throw new Error('Failed to fetch song details');
      const data = response.data.data;
      setSongDetails(data);
      setSongDetailsLoading(false);
      return data;
    } catch (err: any) {
      setSongDetailsError(err.message || 'Failed to fetch song details');
      setSongDetailsLoading(false);
      throw err;
    }
  }, []);

  const clearSongDetails = useCallback(() => {
    setSongDetails(null);
  }, []);

  const fetchVideoDetails = useCallback(async (id: string) => {
    setVideoDetailsLoading(true);
    setVideoDetailsError(null);
    try {
      const response = await api.get(`/api/videos/${id}`);
      if (!response.data.success) throw new Error('Failed to fetch video details');
      const data = response.data.data;
      setVideoDetails(data);
      setVideoDetailsLoading(false);
      return data;
    } catch (err: any) {
      setVideoDetailsError(err.message || 'Failed to fetch video details');
      setVideoDetailsLoading(false);
      throw err;
    }
  }, []);

  const clearVideoDetails = useCallback(() => {
    setVideoDetails(null);
  }, []);

  // Tale details fetcher
  const fetchTaleDetails = useCallback(async (id: string): Promise<TaleDetailsResponse> => {
    setTaleDetailsLoading(true);
    setTaleDetailsError(null);
    try {
      const response = await api.get(`/api/tales/${id}`);
      if (!response.data.success) throw new Error('Failed to fetch tale details');
      const data = response.data.data;
      setTaleDetails(data);
      setTaleDetailsLoading(false);
      return data;
    } catch (err: any) {
      setTaleDetailsError(err.message || 'Failed to fetch tale details');
      setTaleDetailsLoading(false);
      throw err;
    }
  }, []);

  const clearTaleDetails = useCallback(() => {
    setTaleDetails(null);
  }, []);

  // Lullaby details fetcher
  const fetchLullabyDetails = useCallback(async (id: string): Promise<LullabyDetailsResponse> => {
    setLullabyDetailsLoading(true);
    setLullabyDetailsError(null);
    try {
      const response = await api.get(`/api/lullabies/${id}`);
      if (!response.data.success) throw new Error('Failed to fetch lullaby details');
      const data = response.data.data;
      setLullabyDetails(data);
      setLullabyDetailsLoading(false);
      return data;
    } catch (err: any) {
      setLullabyDetailsError(err.message || 'Failed to fetch lullaby details');
      setLullabyDetailsLoading(false);
      throw err;
    }
  }, []);

  // Lullabies methods
  const clearLullabyDetails = useCallback(() => {
    setLullabyDetails(null);
  }, []);

  const value = {
    // Users
    users,
    usersLoading,
    usersError,
    usersPagination,
    fetchUsers,
    updateUser,

    // Videos
    videos,
    videosLoading,
    videosError,
    videosPagination,
    fetchVideos,
    updateVideo,
    createVideo,

    // Songs
    songs,
    songsLoading,
    songsError,
    songsPagination,
    fetchSongs,
    updateSong,
    createSong,

    // Tales
    tales,
    talesLoading,
    talesError,
    talesPagination,
    fetchTales,
    updateTale,
    createTale,

    // Lullabies
    lullabies,
    lullabiesLoading,
    lullabiesError,
    lullabiesPagination,
    fetchLullabies,
    updateLullaby,
    createLullaby,

    // Audios
    audios,
    audiosLoading,
    audiosError,
    audiosPagination,
    fetchAudios,
    updateAudio,
    createAudio,

    // Books
    bookDetails,
    bookDetailsLoading,
    bookDetailsError,
    books,
    booksLoading,
    booksError,
    booksPagination,
    fetchBooks,
    updateBook,
    createBook,

    // Meditations
    meditations,
    meditationsLoading,
    meditationsError,
    meditationsPagination,
    fetchMeditations,
    updateMeditation,
    createMeditation,

    // Promocodes
    promocodes,
    promocodesLoading,
    promocodesError,
    promocodesPagination,
    fetchPromocodes,
    updatePromocode,
    createPromocode,

    // Languages
    languages,
    languagesLoading,
    languagesError,
    languagesPagination,
    fetchLanguages,
    updateLanguage,
    createLanguage,
    deleteLanguage,
    deleteLanguages,

    // Translations
    translations,
    translationsLoading,
    translationsError,
    translationsPagination,
    fetchTranslations,
    updateTranslation,
    createTranslation,
    deleteTranslation,
    deleteTranslations,
    exportTranslations,
    exportTranslationsByLanguage,
    importTranslations,

    // Book details fetcher
    fetchBookDetails,
    clearBookDetails,

    // Meditation details fetcher
    meditationDetails,
    meditationDetailsLoading,
    meditationDetailsError,
    fetchMeditationDetails,
    clearMeditationDetails,

    // Audio details fetcher
    audioDetails,
    audioDetailsLoading,
    audioDetailsError,
    fetchAudioDetails,
    clearAudioDetails,

    // Song details fetcher
    songDetails,
    songDetailsLoading,
    songDetailsError,
    fetchSongDetails,
    clearSongDetails,

    // Video details fetcher
    videoDetails,
    videoDetailsLoading,
    videoDetailsError,
    fetchVideoDetails,
    clearVideoDetails,

    // Tale details fetcher
    taleDetails,
    taleDetailsLoading,
    taleDetailsError,
    fetchTaleDetails,
    clearTaleDetails,
    // Lullaby details fetcher
    lullabyDetails,
    lullabyDetailsLoading,
    lullabyDetailsError,
    fetchLullabyDetails,
    clearLullabyDetails,

    // User details fetcher
    userDetails,
    userDetailsLoading,
    userDetailsError,
    fetchUserDetails,

    // Promocode details fetcher
    promocodeDetails,
    promocodeDetailsLoading,
    promocodeDetailsError,
    fetchPromocodeDetails,
  };

  return <CollectionsContext.Provider value={value}>{children}</CollectionsContext.Provider>;
};
